import { Box, Button, Container, Grid } from '@mui/material';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GET_INVENTORY, GET_INVENTORY_BY_DATE } from 'src/apiService/apiDeclaration';
import { ICONS } from 'src/assets/library';
import AddInventoryForm from 'src/components/form/AddInventoryForm';
import CustomSelectField from 'src/components/formFields/selectField';
import CustomTextField from 'src/components/formFields/textField';
import PageHeader from 'src/components/pageHeader/PageHeader';
import CustomTable from 'src/components/table/table';
import TabTitle from 'src/components/tabTitle/tabTitle';
import { months } from 'src/constants';
import { inventoryTableColumns } from 'src/constants/columnConfig';
import { useModal } from 'src/hooks/useModal';
import usePagination from 'src/hooks/usePagination';

const InventoryPage = () => {
  const { setDialogData, handleModalClose } = useModal();
  const [reportData, setReportData] = useState([]);
  const navigate = useNavigate();
  const date = new Date();
  const [year, setYear] = useState(date.getFullYear());
  const [month, setMonth] = useState(date.getMonth() + 1);

  const { paginationModel, setPaginationModel, setQuerryParams, loading, data, refresh } = usePagination(
    GET_INVENTORY,
    { date: `${year}-${month}` }
  );
  const updatedInventoryTableColumns = [...inventoryTableColumns];

  updatedInventoryTableColumns.push({
    field: 'action',
    headerName: 'Action',
    sortable: false,
    disableColumnMenu: true,
    width: 80,
    renderCell: ({ row }) => {
      return (
        row.stockAdd && (
          <Button
            onClick={() => {
              setDialogData({
                showModal: true,
                title: t('menu.editInventory'),
                child: <AddInventoryForm onSuccess={() => onSuccess()} data={row} />,
              });
            }}
            sx={{ color: '#000' }}
          >
            {ICONS.edit}
          </Button>
        )
      );
    },
  });

  const getInventoryReport = async () => {
    try {
      let resp = await GET_INVENTORY_BY_DATE(`${year}-${month}`);
      setReportData(resp.data);
    } catch (error) {
      setReportData([]);
      console.log('error', error);
    }
  };

  const onSuccess = () => {
    refresh();
    handleModalClose();
  };

  useEffect(() => {
    setQuerryParams({ date: `${year}-${month}` });
    getInventoryReport();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);
  const handleNavigate = (data) => {
    navigate('/pdf', { state: data });
  };

  return (
    <Container>
      <Grid container spacing={2}>
        <TabTitle title={'Inventory'} />
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: {
              xs: 'flex-start',
              sm: 'center',
            },
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
          }}
        >
          <PageHeader pageTitle={t('navMenu.inventory')} />
          <Grid
            container
            sm={10}
            xs={12}
            sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2, gap: { sm: 0, xs: 2 } }}
          >
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ display: 'flex', justifyContent: { sm: 'flex-end', xs: 'space-between' }, gap: 2 }}
            >
              <Grid item xs={6} sm={5} sx={{ display: 'flex', alignItems: 'center' }}>
                <CustomTextField xs={4} sm={2} value={year} />
                <Button
                  xs={0.5}
                  variant="contained"
                  sx={{
                    padding: '0.25rem',
                    margin: '0 0.5rem',
                    fontSize: { sm: '1.25rem', xs: '1rem' },
                    minWidth: '30px',
                    height: '30px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  disabled={year === date.getFullYear()}
                  onClick={() => {
                    setYear(year + 1);
                  }}
                >
                  +
                </Button>
                <Button
                  xs={0.5}
                  variant="contained"
                  sx={{
                    padding: '0.25rem',
                    margin: '0 0.5rem',
                    fontSize: { sm: '1.25rem', xs: '1rem' },
                    minWidth: '30px',
                    height: '30px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onClick={() => {
                    setYear(year - 1);
                  }}
                >
                  -
                </Button>
              </Grid>
              <CustomSelectField
                xs={6}
                sm={1}
                onChange={(e) => setMonth(e.target.value)}
                optionsArray={months}
                value={month}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={5}
              sx={{ display: 'flex', justifyContent: { sm: 'flex-end', xs: 'space-between' }, gap: 2 }}
            >
              <Button
                xs={6}
                sm={4}
                sx={{ fontsize: { sm: '1.25rem', xs: '0.5rem' } }}
                variant="contained"
                onClick={() => handleNavigate(reportData)}
                disabled={reportData.length === 0 || loading}
              >
                {t('inventoryPage.generatePDF')}
              </Button>
              <Button
                xs={6}
                sm={4}
                sx={{ fontsize: { sm: '1.25rem', xs: '0.5rem' } }}
                variant="contained"
                onClick={() =>
                  setDialogData({
                    showModal: true,
                    title: t('inventoryPage.modalHeading'),
                    child: <AddInventoryForm onSuccess={() => onSuccess()} />,
                  })
                }
              >
                {t('inventoryPage.addInventory')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          xs={12}
          sx={{
            marginTop: '1rem',
            marginLeft: '1rem',
            display: 'flex',
            flexDirection: {
              md: 'row',
              xs: 'column',
            },
            borderRadius: '5%',
            bgcolor: '#eaeaeb',
            height: {
              md: '3rem',
              xs: '2rem',
            },
            fontSize: {
              md: '1rem',
              xs: '0.6rem',
            },
          }}
        >
          <Box
            sx={{
              width: '20%',
              height: '100%',
              borderRadius: '5%',
              bgcolor: '#ACD629',
              color: '#fff',
              fontWeight: 600,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {t('inventoryPage.initialStock')}
          </Box>
          <Box
            sx={{
              width: {
                md: '20%',
                xs: '18%',
              },
              height: '100%',
              display: 'flex',
              paddingLeft: '0.6rem',
              alignItems: 'center',
            }}
          >
            {t('inventoryPage.cement')}: {reportData[0] ? reportData[0].totalCementAdd : '  -'}
          </Box>
          <Box
            sx={{
              width: {
                md: '20%',
                xs: '18%',
              },
              height: '100%',
              display: 'flex',
              paddingLeft: '0.6rem',
              alignItems: 'center',
            }}
          >
            {t('inventoryPage.sand')}: {reportData[0] ? reportData[0].totalSandAdd : '  -'}
          </Box>
          <Box
            sx={{
              width: {
                md: '20%',
                xs: '22%',
              },
              height: '100%',
              display: 'flex',
              paddingLeft: {
                md: '0.6rem',
                xs: 0,
              },
              alignItems: 'center',
            }}
          >
            {t('inventoryPage.gravel815')}: {reportData[0] ? reportData[0].totalGravel815Add : '  -'}
          </Box>
          <Box
            sx={{
              width: {
                md: '20%',
                xs: '22%',
              },
              height: '100%',
              display: 'flex',
              paddingLeft: {
                md: '0.6rem',
                xs: 0,
              },
              alignItems: 'center',
            }}
          >
            {t('inventoryPage.gravel1525')}: {reportData[0] ? reportData[0].totalGrave1525Add : '  -'}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <CustomTable
            loading={loading}
            isServerSidePagination
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            columns={updatedInventoryTableColumns}
            rows={data.rows}
            rowCount={data.rowCount}
            isRowColored={true}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default InventoryPage;
